import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Users, Calendar, User } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminNewsletter = ({ password, members = [], onBack }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [recipientType, setRecipientType] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [sending, setSending] = useState(false);

  const toggleMember = (memberId) => {
    setSelectedMemberIds((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  const send = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Bitte Betreff und Nachricht eingeben');
      return;
    }
    if (recipientType === 'individual' && selectedMemberIds.length === 0) {
      toast.error('Bitte mindestens einen Empfänger auswählen');
      return;
    }
    if ((recipientType === 'before_date' || recipientType === 'after_date') && !filterDate) {
      toast.error('Bitte ein Datum auswählen');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/admin/send-newsletter`,
        {
          subject,
          message,
          recipientType,
          filterDate: filterDate || null,
          selectedMemberIds,
        },
        { headers: { 'X-Admin-Password': password } }
      );

      toast.success(response.data.message);
      setSubject('');
      setMessage('');
      setSelectedMemberIds([]);
      setFilterDate('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden der Rundmail');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" onClick={onBack} className="mb-4" data-testid="newsletter-back-btn">
          ← Zurück zum Admin
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-6 w-6 text-blue-600" />
              Rundmail versenden
            </CardTitle>
            <CardDescription>Senden Sie Informationen an Ihre Mitglieder</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Empfänger-Auswahl */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Empfänger auswählen
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Button
                  variant={recipientType === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setRecipientType('all')}
                  className="justify-start"
                  data-testid="newsletter-recipient-all"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Alle Mitglieder
                </Button>
                <Button
                  variant={recipientType === 'before_date' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setRecipientType('before_date')}
                  className="justify-start"
                  data-testid="newsletter-recipient-before"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Vor Datum
                </Button>
                <Button
                  variant={recipientType === 'after_date' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setRecipientType('after_date')}
                  className="justify-start"
                  data-testid="newsletter-recipient-after"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Ab Datum
                </Button>
                <Button
                  variant={recipientType === 'individual' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setRecipientType('individual')}
                  className="justify-start"
                  data-testid="newsletter-recipient-individual"
                >
                  <User className="h-4 w-4 mr-2" />
                  Individuell
                </Button>
              </div>
            </div>

            {/* Datumsfilter */}
            {(recipientType === 'before_date' || recipientType === 'after_date') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {recipientType === 'before_date' ? 'Mitglieder vor dem:' : 'Mitglieder ab dem:'}
                </label>
                <Input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                  className="max-w-xs"
                  data-testid="newsletter-filter-date"
                />
              </div>
            )}

            {/* Individuelle Auswahl */}
            {recipientType === 'individual' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mitglieder auswählen ({selectedMemberIds.length} ausgewählt)
                </label>
                <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {members.length === 0 ? (
                    <p className="text-gray-500 text-sm p-2">Keine Mitglieder vorhanden</p>
                  ) : (
                    members.map((member) => (
                      <div
                        key={member.id}
                        onClick={() => toggleMember(member.id)}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          selectedMemberIds.includes(member.id)
                            ? 'bg-blue-100 text-blue-800'
                            : 'hover:bg-gray-100'
                        }`}
                        data-testid={`newsletter-member-${member.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedMemberIds.includes(member.id)}
                          onChange={() => {}}
                          className="rounded"
                        />
                        <span className="font-medium">{member.name}</span>
                        <span className="text-gray-500 text-sm">({member.email})</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Info-Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-blue-800 text-sm">
                {recipientType === 'all' &&
                  `📬 Die Rundmail wird an alle ${members.length} Mitglieder gesendet.`}
                {recipientType === 'before_date' &&
                  filterDate &&
                  `📬 Die Rundmail wird an Mitglieder gesendet, die vor dem ${new Date(
                    filterDate
                  ).toLocaleDateString('de-DE')} beigetreten sind.`}
                {recipientType === 'after_date' &&
                  filterDate &&
                  `📬 Die Rundmail wird an Mitglieder gesendet, die ab dem ${new Date(
                    filterDate
                  ).toLocaleDateString('de-DE')} beigetreten sind.`}
                {recipientType === 'individual' &&
                  `📬 Die Rundmail wird an ${selectedMemberIds.length} ausgewählte Mitglieder gesendet.`}
                {(recipientType === 'before_date' || recipientType === 'after_date') &&
                  !filterDate &&
                  '⚠️ Bitte wählen Sie ein Datum aus.'}
              </p>
            </div>

            {/* Betreff */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Betreff</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="z.B. Wichtige Information zu Ihrer Mitgliedschaft"
                data-testid="newsletter-subject"
              />
            </div>

            {/* Nachricht */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nachricht</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ihre Nachricht an die Mitglieder..."
                rows={8}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="newsletter-message"
              />
            </div>

            {/* Senden Button */}
            <Button
              onClick={send}
              disabled={sending || !subject.trim() || !message.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="newsletter-send-btn"
            >
              {sending ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Wird gesendet...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Rundmail senden
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminNewsletter;
